import { Inline, RadioCard, RadioGroup, Stack, Text, vars } from '@etg/wings';
import { price as priceUtil } from '@eti/utils';
import { css } from '@eti/styles';
import { useDirection, useTranslation } from '@eti/providers';
import { ScreenReaderPrice } from '@eti/components';
import { CABIN_UPGRADE } from '../../../products';
import { getAvailablePerks, getSegmentUpgradeOptionKey, getSegmentUpgradePricePerTraveler } from '../../utils/utils';
import { screenReaderOnly } from '../../../common/utils/styles';
import CabinUpgradePerks from './CabinUpgradePerks';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  inputs
} = vars.colors;
const radioCardStyles = expanded => css`
  border-color: ${expanded ? inputs.main : inputs.border};
  border-radius: ${vars.card.borderRadius};
  margin-bottom: 16px;
`;
const upgradeOptionLabelStyles = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const priceLabelTextStyle = isRTL => css`
  text-align: ${isRTL ? 'inherit' : 'end'};
`;
const getSelectedRadioGroupValue = _ref => {
  var _Object$values$find;
  let {
    boundIndex,
    segmentIndex,
    selectedCabinUpgradeOptions
  } = _ref;
  const selectedValue = ((_Object$values$find = Object.values(selectedCabinUpgradeOptions).find(item => item.boundIndex === boundIndex && item.segmentIndex === segmentIndex)) === null || _Object$values$find === void 0 ? void 0 : _Object$values$find.value) || '';
  return selectedValue;
};
const CabinUpgradeAvailableOptions = _ref2 => {
  var _segmentUpgrade$upgra;
  let {
    drawerTexts,
    segmentKey,
    segmentUpgrade,
    selectedCabinUpgradeOptions,
    setSelectedCabinUpgradeOptions,
    setUnselectedCabinUpgrades,
    originDestinationAirportAriaLabelText
  } = _ref2;
  const {
    isRTL
  } = useDirection();
  const {
    t
  } = useTranslation();
  if (!(segmentUpgrade !== null && segmentUpgrade !== void 0 && (_segmentUpgrade$upgra = segmentUpgrade.upgradeOptions) !== null && _segmentUpgrade$upgra !== void 0 && _segmentUpgrade$upgra.length)) {
    return null;
  }
  const {
    drawerCardSelectionTitle,
    perks = [],
    perPersonLabel
  } = drawerTexts;
  const {
    boundIndex,
    segmentIndex,
    upgradeOptions = []
  } = segmentUpgrade || {};
  const handleChange = value => {
    var _Number, _value$match, _upgradeOptions$optio;
    const optionIndex = (_Number = Number((_value$match = value.match(/option-(\d+)/)) === null || _value$match === void 0 ? void 0 : _value$match[1])) !== null && _Number !== void 0 ? _Number : null;
    const selectedOption = {
      upgradeOption: (_upgradeOptions$optio = upgradeOptions[optionIndex]) !== null && _upgradeOptions$optio !== void 0 ? _upgradeOptions$optio : null,
      value
    };
    setSelectedCabinUpgradeOptions(prev => {
      const filteredSelections = Object.keys(prev).reduce((acc, key) => {
        if (!key.includes(segmentKey)) {
          return {
            ...acc,
            [key]: prev[key]
          };
        }
        return acc;
      }, {});
      return {
        ...filteredSelections,
        [value]: {
          ...selectedOption,
          boundIndex,
          segmentIndex
        }
      };
    });
    setUnselectedCabinUpgrades(prev => {
      return Object.keys(prev).reduce((acc, key) => {
        if (key !== segmentKey) {
          acc[key] = prev[key];
        }
        return acc;
      }, {});
    });
  };
  return /*#__PURE__*/_jsxs(RadioGroup, {
    name: `${boundIndex}-${segmentIndex}`,
    onChange: event => {
      var _event$target;
      return handleChange(event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    },
    selectedValue: getSelectedRadioGroupValue({
      selectedCabinUpgradeOptions,
      boundIndex,
      segmentIndex
    }),
    children: [/*#__PURE__*/_jsx(RadioGroup.Legend, {
      className: screenReaderOnly,
      children: `${originDestinationAirportAriaLabelText} ${drawerCardSelectionTitle}`
    }), upgradeOptions.map((upgradeOption, optionIndex) => {
      var _selectedCabinUpgrade;
      const upgradeOptionKey = getSegmentUpgradeOptionKey({
        boundIndex,
        segmentIndex,
        optionIndex
      });
      const selectedOption = {
        upgradeOption,
        value: upgradeOptionKey
      };
      const price = getSegmentUpgradePricePerTraveler(upgradeOption === null || upgradeOption === void 0 ? void 0 : upgradeOption.travelers);
      const hasPrice = typeof price === 'number' && !Number.isNaN(price);
      const availablePerks = getAvailablePerks(perks, upgradeOption === null || upgradeOption === void 0 ? void 0 : upgradeOption.upgradeType);
      const shouldDisplayPerks = Boolean(((_selectedCabinUpgrade = selectedCabinUpgradeOptions[upgradeOptionKey]) === null || _selectedCabinUpgrade === void 0 ? void 0 : _selectedCabinUpgrade.value) && availablePerks.length);
      const formattedPrice = priceUtil.formatPrice(price);
      return /*#__PURE__*/_jsx(RadioCard, {
        className: radioCardStyles(Boolean(selectedCabinUpgradeOptions[upgradeOptionKey])),
        label: /*#__PURE__*/_jsxs(Inline, {
          className: upgradeOptionLabelStyles,
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "14-bold",
            children: upgradeOption === null || upgradeOption === void 0 ? void 0 : upgradeOption.upgradeType
          }), hasPrice && /*#__PURE__*/_jsxs(Stack, {
            "data-testid": `${CABIN_UPGRADE.name}-${upgradeOptionKey}-price`,
            spacing: 0,
            children: [/*#__PURE__*/_jsx(ScreenReaderPrice, {
              price: formattedPrice
            }), /*#__PURE__*/_jsx(Text, {
              "aria-hidden": "true",
              as: "bdi",
              variant: "18-emphasized",
              children: `+${formattedPrice}`
            }), /*#__PURE__*/_jsx(Text, {
              as: "bdi",
              className: priceLabelTextStyle(isRTL),
              children: perPersonLabel
            })]
          })]
        }),
        value: selectedOption.value,
        children: shouldDisplayPerks && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            className: screenReaderOnly,
            children: t('ScreenReader.Product.CabinUpgrade.Included.Perks.Text', availablePerks.length)
          }), /*#__PURE__*/_jsx(CabinUpgradePerks, {
            ariaLabel: t('ScreenReader.Product.CabinUpgrade.Available.Perks.Text'),
            availablePerks: availablePerks,
            segmentKey: segmentKey
          })]
        })
      }, upgradeOptionKey);
    })]
  });
};
export default CabinUpgradeAvailableOptions;
//# sourceMappingURL=CabinUpgradeAvailableOptions.js.map